import axios from 'axios';

// 根据环境变量设置 API 基础 URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加 JWT token 和调试
api.interceptors.request.use(
  (config) => {
    // 从 localStorage 获取 token
    const token = localStorage.getItem('token');
    console.log('Token from localStorage:', token);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Authorization header:', config.headers.Authorization);
    }
    console.log('API Request:', config.method.toUpperCase(), config.url);
    if (config.data) {
      console.log('Request Body:', config.data);
    }
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理 401 错误
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 清除登录状态
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // 跳转到登录页
      window.location.href = '/shortdrama/login';
    }
    return Promise.reject(error);
  }
);

// 项目管理 API
export const projectApi = {
  getAll: () => api.get('/projects'),
  getById: (id) => api.get(`/projects/${id}`),
  create: (project) => api.post('/projects', project),
  update: (id, project) => api.put(`/projects/${id}`, project),
  delete: (id) => api.delete(`/projects/${id}`),
  // 保存/加载项目完整数据
  save: (id, data) => api.post(`/projects/${id}/save`, data),
  load: (id) => api.get(`/projects/${id}/load`),
  // 保存项目为 .manju 文件
  saveToFile: (id) => api.post(`/projects/${id}/save-file`, null, { responseType: 'blob' }),
  // 导出/导入项目（.manju 格式）
  export: (id) => api.get(`/projects/${id}/export`, { responseType: 'blob' }),
  import: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/projects/${id}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// 剧本管理 API
export const scriptApi = {
  get: (projectId) => api.get(`/projects/${projectId}/script`),
  save: (projectId, content, episodes) => 
    api.post(`/projects/${projectId}/script`, { project_id: projectId, content, episodes }),
};

// 资产管理 API
export const assetApi = {
  getAll: (projectId) => api.get(`/projects/${projectId}/assets`),
  create: (projectId, asset) => api.post(`/projects/${projectId}/assets`, asset),
  update: (projectId, assetId, asset) => api.put(`/projects/${projectId}/assets/${assetId}`, asset),
  delete: (projectId, assetId) => api.delete(`/projects/${projectId}/assets/${assetId}`),
};

// 分镜管理 API
export const shotApi = {
  getAll: (projectId) => api.get(`/shots/project/${projectId}`),
  getByProjectId: (projectId) => api.get(`/shots/project/${projectId}`),
  save: (projectId, shots) => api.post(`/shots/project/${projectId}`, shots),
  // 上传视频
  uploadVideo: (projectId, shotIndex, formData) => api.post(`/shots/project/${projectId}/upload/${shotIndex}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // 删除视频
  deleteVideo: (projectId, shotIndex) => api.delete(`/shots/project/${projectId}/video/${shotIndex}`),
  // 上传分镜图
  uploadStoryboardImage: (projectId, shotId, formData) => api.post(`/shots/project/${projectId}/upload-storyboard-image/${shotId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // 获取分镜组（用于视频制作页面）
  getGroups: (projectId) => api.get(`/shots/project/${projectId}/groups`),
};

// AI 功能 API
export const aiApi = {
  analyzeScript: (script, config) => api.post('/ai/analyze-script', { script, config }),
  generateStoryboard: (script, projectId, customPrompt, episode) => api.post('/generate-storyboard', { script, project_id: projectId, custom_prompt: customPrompt, episode: episode === 'all' ? '' : episode }),
  startGenerateStoryboard: (script, projectId, customPrompt, episode) => api.post('/generate-storyboard/start', { script, project_id: projectId, custom_prompt: customPrompt, episode: episode === 'all' ? '' : episode }),
  getStoryboardStatus: (taskId) => api.get(`/generate-storyboard/status/${taskId}`),
  generateImage: (prompt, config) => api.post('/ai/generate-image', { prompt, config }),
  getImageGenerationStatus: (taskId, taskData) => api.get(`/ai/generate-image/status/${taskId}`, { 
    params: taskData 
  }),
  generateVideo: (prompt, config) => api.post('/ai/generate-video', { prompt, config }),
  // Seedance视频生成
  createVideoGeneration: (data) => api.post('/ai/generate-video', data),
  getVideoGenerationStatus: (taskId) => api.get(`/ai/generate-video/status/${taskId}`),
  // 分镜图生成
  createStoryboardImage: (data) => api.post('/ai/generate-storyboard-image', data),
  // Token使用统计
  getProjectTokenUsage: (projectId) => api.get(`/ai/token-usage/project/${projectId}`),
  getUserTokenUsage: () => api.get('/ai/token-usage/user'),
};

// 文件上传 API
export const uploadApi = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// 项目成员管理 API
// （已移除流程审核和团队管理相关代码）

// 系统配置 API
export const systemConfigApi = {
  getAISettings: () => api.get('/config/ai-settings'),
  updateConfigs: (configs) => api.post('/config', { configs }),
};

// 订单 API
export const orderApi = {
  getAll: (params = {}) => {
    const urlParams = new URLSearchParams();
    if (params.status) urlParams.append('status', params.status);
    if (params.role) urlParams.append('role', params.role);
    return api.get(`/orders?${urlParams.toString()}`);
  },
  getPublic: () => api.get('/orders/public'),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  accept: (id) => api.post(`/orders/${id}/accept`),
  submit: (id, data) => api.post(`/orders/${id}/submit`, data),
  review: (id, data) => api.post(`/orders/${id}/review`, data),
  complete: (id) => api.post(`/orders/${id}/complete`),
  cancel: (id) => api.post(`/orders/${id}/cancel`),
  creatorCancel: (id) => api.post(`/orders/${id}/creator-cancel`),
  resubmit: (id, data) => api.post(`/orders/${id}/resubmit`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  getAssets: (id) => api.get(`/orders/${id}/assets`),
  uploadAsset: (id, formData) => api.post(`/orders/${id}/assets`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteAsset: (orderId, assetId) => api.delete(`/orders/${orderId}/assets/${assetId}`),
  getComments: (id) => api.get(`/orders/${id}/comments`),
  addComment: (id, data) => api.post(`/orders/${id}/comments`, data),
  saveReviewComment: (id, data) => api.post(`/orders/${id}/save-review-comment`, data),
};

export const pointsApi = {
  getTransactions: (params = {}) => {
    const urlParams = new URLSearchParams();
    if (params.page) urlParams.append('page', params.page);
    if (params.per_page) urlParams.append('per_page', params.per_page);
    return api.get(`/points/transactions?${urlParams.toString()}`);
  },
  getStatistics: () => api.get('/points/transactions/statistics'),
};

export { api };
export default api;
